import * as Types from '../gql/_types.js';

import { DocumentModule } from "./init";
import { mapArrayToStateObject } from "src/utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'src/state/_types';
import { subscriptions } from 'src/state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'src/state/gql/utils';
import { SetMetaInput } from 'src/state/Module';

const Module = DocumentModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetDocumentsQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.documents.errors = [];
    state.documents.loading = true;
    try {
        let { limit, offset, orderBy } = state.documents.meta;
        let originalPayload = { limit, offset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetDocuments({...originalPayload, ...finalPayload})
        state.documents.meta.total = meta.aggregate?.count || 0;
        state.documents.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.documents.errors = handleGraphqQLErrorResponse(e);
    }
    state.documents.loading = false;
}

export const resetState: Action = ({state}) => {
    state.documents.errors = []
    state.documents.loading = false
    state.documents.items = {}
    state.documents.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.documents.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.documents.resetMeta()
    actions.documents.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.documents.errors = [];
    state.documents.loadingMore = true;
    try {
        let { limit, orderBy, page } = state.documents.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetDocuments({...originalPayload, ...finalPayload})
        state.documents.meta.total = meta.aggregate?.count || 0;
        state.documents.meta.page = newPage;
        state.documents.meta.offset = newOffset;
        state.documents.items = { ...state.documents.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.documents.errors = handleGraphqQLErrorResponse(e);
    }
    state.documents.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetDocumentsQueryVariables> = async ({state, effects}, payload) => {
    state.documents.errors = [];
    state.documents.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetDocuments(payload)
        state.documents.meta.total = meta.aggregate?.count || 0;
        state.documents.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.documents.errors = handleGraphqQLErrorResponse(e);
    }
    state.documents.loading = false;
}

export const getItem: AsyncAction<Types.GetDocumentQueryVariables,Types.GetDocumentQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetDocument(payload);
        return item;
    } catch (e: any) {
        state.documents.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetDocumentQueryVariables> = async ({state, actions}, payload) => {
    state.documents.loading = true;
    try {
        let item = await actions.documents.getItem(payload);
        if (item) {
            state.documents.currentItemId = item.id;
            state.documents.items[item.id] = item;
        }
    } catch (e: any) {
        state.documents.errors = handleGraphqQLErrorResponse(e);
    }
    state.documents.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.documents.currentItemId = payload
}

export const deselectItem: Action = async ({state}) => {
    state.documents.currentItemId = null
}

export const setMeta: Action<SetMetaInput<Types.DocumentFragment>> = async ({state}, meta) => {
    state.documents.meta = {
        ...state.documents.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateDocumentMutationVariables['object'],Types.CreateDocumentMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateDocumentMutationVariables['object']>(payload);
    state.documents.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateDocument({object:finalPayload});
        return item;
    } catch (e: any) {
        state.documents.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateDocumentMutationVariables,Types.UpdateDocumentMutation['item']> = async ({state, effects}, payload) => {
    state.documents.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateDocument(payload);
        return item;
    } catch (e: any) {
        state.documents.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteDocumentMutationVariables,Types.DeleteDocumentMutation['item']> = async ({state, effects}, payload) => {
    state.documents.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteDocument(payload);
        return item;
    } catch (e: any) {
        state.documents.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetDocumentsMetaSubscriptionVariables> = async ({actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetDocumentsMetaSubscription, Types.SyncGetDocumentsMetaSubscriptionVariables>(
        'syncGetDocumentsMeta', 
        subscriptions.SyncGetDocumentsMeta, 
        { where }, 
        actions.documents.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetDocumentsMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetDocumentsMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.documents.errors = [ ...state.documents.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.documents.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetDocumentsSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, orderBy } = state.documents.meta;
    let originalPayload = { limit, offset, order_by: orderBy };
    state.documents.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetDocumentsSubscription, Types.SyncGetDocumentsSubscriptionVariables>(
        'syncGetDocuments', 
        subscriptions.SyncGetDocuments, 
        {...originalPayload, ...payload}, 
        actions.documents.onItemsData,
        () => { state.documents.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetDocuments');
    state.documents.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetDocumentsSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.documents.errors = [ ...state.documents.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.documents.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetDocumentsSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.documents.syncGetItems(finalPayload);
    actions.documents.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.documents.unsubItemsData();
    actions.documents.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetDocumentSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.documents.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetDocumentSubscription, Types.SyncGetDocumentSubscriptionVariables>(
        'syncGetDocument', 
        subscriptions.SyncGetDocument, 
        payload, 
        actions.documents.onItemData,
        () => { state.documents.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetDocument');
    state.documents.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetDocumentSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.documents.errors = [ ...state.documents.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.documents.currentItemId = payload.data.item.id;
        state.documents.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetDocumentSubscriptionVariables> = async ({actions}, payload) => {
    actions.documents.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.documents.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    deselectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};